import _ from 'lodash';

import geolocation from '../../../common/wrappers/angularjs-geolocation';

import locationRes from '../resources/location.resource';
import analyticsServices from '../../analytics/analytics.service';
import ResourceCollection from '../resource.collection';
import { getNearestLocationCity } from './utils';
import LocationStorage from './location-storage';
import {noop} from "../common.utils";

/**
 * How long to wait for geolocation until it is considered as failed.
 * @type {Number}
 */
const GEOLOCATION_TIMEOUT = 20000;

/**
 * Event that can be broadcasted when geolocation request is made.
 * @type {string}
 */
const GEOLOCATION_REQUESTED_EVENT = 'geolocation-requested';

/**
 * Calculate distances from coordinates to given locations and
 * return a list of the locations with distances set .
 *
 * @param {Array.<Location>} locations
 * @param {?Object} coordinates
 * @param {Number} coordinates.lat
 * @param {Number} coordinates.lon
 * @return {Array.<Location>}
 */
function calculateDistances(locations, coordinates) {
  const ret = [];
  if (coordinates == null) {
    // There's no log service here and it's purely development case
    // eslint-disable-next-line no-console
    console.error("No coordinates given for distance calculation");
    return ret;
  }

  for (const location of locations) {
    location.distance = calculateDistance(location.address.coordinates, coordinates);
  }

  return locations;
}

/**
 * Calculate distance between two sets of coordinates. Each set must be an object with lat and lon keys.
 * @param {Number} lat1
 * @param {Number} lon1
 * @param {Number} lat2
 * @param {Number} lon2
 * @return {Number}
 */
function calculateDistance({lat: lat1, lon: lon1}, {lat: lat2, lon: lon2}) {
  // Use Spherical law of cosines, which is precise enough
  // Could also use just Pythagoras’ theorem if this seems too slow

  const φ1 = _toRadians(lat1),
    φ2 = _toRadians(lat2),
    Δλ = _toRadians(lon2 - lon1),
    R = 6371; // gives d in km
  return Math.acos((Math.sin(φ1) * Math.sin(φ2)) + (Math.cos(φ1) * Math.cos(φ2) * Math.cos(Δλ))) * R;
}

function _toRadians(value) {
  return value * Math.PI / 180;
}

function isValidCoordinates(coordinates) {
  if (coordinates == null) {
    return false;
  }
  const keys = Object.keys(coordinates);
  if (!keys.includes('latitude') || !keys.includes('longitude')) {
    return false;
  }
  // TODO: check that coordinates are somewhat sane
  return true;
}


class GeolocationService {
  constructor(
    $rootScope, $q, $timeout, $log, LocationResource,
    geolocation, geolocation_msgs, Analytics, $sessionStorage,
    $http, $httpParamSerializer
  ) {
    'ngInject';

    this.$rootScope = $rootScope;
    this.$q = $q;
    this.$timeout = $timeout;
    this.$log = $log;
    this.LocationResource = LocationResource;
    this.GeolocationService = geolocation;
    this.GeolocationMsgs = geolocation_msgs;
    this.Analytics = Analytics;
    this.locationStorage = new LocationStorage($log, $sessionStorage);
    this.$http = $http;
    this.$httpParamSerializer = $httpParamSerializer;


    /**
     * Have locations been fetched?
     * @type {boolean}
     */
    this.locationsFetched = false;

    /**
     * Is geolocation unavailable for some reason?
     * @type {boolean}
     */
    this.locationUnavailable = false;

    /**
     * The obtained user's location, if available
     * @type {?Object}
     */
    this.userLocation = null;

    // Cache retrieved locations
    this._cachedLocations = null;
  }

  /**
   * Fetch nearby locations. Gets user location and all locations. Calculates distances for all locations and returns
   * the nearest. Sets the distance attribute for locations in the location resource.
   * @param {Number} amount Amount of nearest locations to return.
   * @returns {Promise.<Array.<Location>>} Promise that is resolved with nearest locations or rejected if geolocation
   *   is unavailable.
   */
  getNearbyLocations(amount=2) {
    if (this.locationsFetched) {
      return this.$q.when(this.getNearest(amount));
    }
    else if (this.locationUnavailable) {
      return this.$q.reject();
    }

    const userLocationPromise = this.userLocation != null ? this.$q.when(this.userLocation) : this.getLocation();

    const locationResourcePromise = this.LocationResource.getAll().then(data => {
      this._cachedLocations = new ResourceCollection(this.calculateDistances(data.list)).makeImmutable();
    });

    return this.$q.all([userLocationPromise, locationResourcePromise]).then(() => {
      return this.getNearest(amount);
    }).finally(() => {
      this.Analytics.setLocationUnavailable(this.locationUnavailable);
    })
    .catch(noop);
  }

  /**
   * Fetches the coordinates data from geolocation and saves it to locationStorage
   * @returns The original geolocation data
   */
  getAndCacheGeolocationData() {
    return this.GeolocationService.getLocation().then(data => {
      // Cache the coordinates value for future use
      this.$log.debug(`Received geolocation coordinates: latitude ${data.coords.latitude}, longitude ${data.coords.longitude}`);
      const { latitude, longitude } = data.coords;
      this.locationStorage.setCoordinates({coords: {latitude, longitude}});
      return getNearestLocationCity(latitude, longitude, this.$http, this.$httpParamSerializer).then((response) => {
        // Also cache the nearest city for future use
        const { city } = response.data;
        this.$log.debug(`Setting geolocation city to: ${city}`);
        this.locationStorage.setGeolocationCity(city);
        // Resolve the original geolocation data;
        return data;
      })
    });
  }

  /**
   * Wrapper for geoLocation.getLocation.
   *
   * If geolocation is not obtained after a certain time, abandon all hope (see comment below).
   *
   * @param {number} geolocationTimeout timeout for geolocation in milliseconds.
   * @returns {Promise} A promise to be resolved with geoLocation data.
   */
  getLocation(geolocationTimeout = GEOLOCATION_TIMEOUT) {
    let finished = false;

    return this.$q((resolve, reject) => {
      let geoLocationPromise;
      // Due to severe time and budget constraints the caching is coded on top of the old structure
      // so that we can preserve the old functionality instead of properly restructuring some of it.
      const savedCoordinatesObj = this.locationStorage.getCoordinates();
      if (!savedCoordinatesObj || !isValidCoordinates(savedCoordinatesObj.coords)) {
        geoLocationPromise = this.getAndCacheGeolocationData();
      } else {
        geoLocationPromise = new Promise((resolve) => {
          this.$log.debug(`Using cached coordinates: ${savedCoordinatesObj}`);
          resolve(savedCoordinatesObj);
        })
      }

      geoLocationPromise.then(data => {
        this.userLocation = data;
        // Distance calculation uses lat and lon
        this.userLocation.coords.lat = this.userLocation.coords.latitude;
        this.userLocation.coords.lon = this.userLocation.coords.longitude;
        this.locationUnavailable = false;
        resolve(data);
      })
      .catch(reason => {
        // Log user denied error
        if (reason === this.GeolocationMsgs['errors.location.permissionDenied']) {
          this.Analytics.postEvent('paikannus', 'epäonnistunut', 'estetty');
        }
        else if (reason === this.GeolocationMsgs['errors.location.unsupportedBrowser']) {
          this.Analytics.postEvent('paikannus', 'epäonnistunut', 'ei selaintukea');
        }
        else if (reason === this.GeolocationMsgs['errors.location.positionUnavailable']) {
          this.Analytics.postEvent('paikannus', 'epäonnistunut', 'ei paikkatietoa');
        }
        else if (reason === this.GeolocationMsgs['errors.location.timeout']) {
          this.Analytics.postEvent('paikannus', 'epäonnistunut', 'aikakatkaisu');
        }

        this.$log.debug(`Getting user location failed: ${reason}`);
        this.locationUnavailable = true;
        this.userLocation = null;
        reject(reason);
      })
      .finally(() => {
        finished = true;
      });

      // If geolocation has not finished in a certain time, assume there is no geolocation.
      // This is done because on some browsers the geolocation request never finishes, if the
      // user dismisses the geolocation dialog. This is a last case scenario hard limit to
      // abandon attempts at geolocation.

      this.$timeout(() => {
        if (!finished) {
          this.$log.debug('User did not answer geolocation in time, acting like it is disabled.');
          reject();
        }
      }, geolocationTimeout);
    });
  }

  /**
   * Get the given amount of nearest locations from the location resource.
   * @param {Number} amount
   */
  getNearest(amount) {
    return _.sortBy(this._cachedLocations.list, ['distance']).slice(0, amount);
  }

  /**
   * Calculate distances to given locations and return a list of the locations with distances set, if user's location
   * is available. If coordinates are not provided, userLocation is used.
   *
   * @param {Array.<Location>} locs
   * @param {?Object} coordinates
   * @param {Number} coordinates.lat
   * @param {Number} coordinates.lon
   * @return {Array.<Location>}
   */
  calculateDistances(locs, coordinates=null) {

    if (coordinates == null && this.userLocation != null) {
      coordinates = this.userLocation.coords;
    }

    return calculateDistances(locs, coordinates);
  }

  /**
   * Mutator, changes the state of the GeolocationService
   *
   *    This function is called by $rootScope.$on('error', ...)
   *    to check if an error happened in locating the user or if
   *    location is otherwise unavailable, so we can react to it.
   *
   * @param {String} data message from $rootScope.$broadcast
   */
  checkIfLocationUnavailable(data) {
    if (
      data === this.GeolocationMsgs['errors.location.timeout']
      || data === this.GeolocationMsgs['errors.location.notFound']
      || data === this.GeolocationMsgs['errors.location.permissionDenied']
      || data === this.GeolocationMsgs['errors.location.unsupportedBrowser']
    ) {
      this.$log.debug('Location unavailable');
      this.locationUnavailable = true;
    }
  }
}

const mod = angular.module('app.common.geolocation', [
  geolocation,
  locationRes,
  analyticsServices
])

/**
 * Encapsulation Geolocation dependant services
 */
.service('GeolocationService', GeolocationService);

export default mod.name;

export {
  calculateDistances,
  GEOLOCATION_REQUESTED_EVENT
};
